import { isArray } from "util";
import { AreaExtType, ExtType } from "./Macro";

export interface BinderItem {
  binderInfo: BinderInfo;
  articleList: Article[];
  corpTotalCount: string; // 企業数
  industryTotalCount: string; // 業種数
  saveArticleTotalCount: string; // 保存記事数
  statisticsTotalCount: string; // 統計数
  externalPageTotalCount: string; // 外部ページ数
  thumbnailUrl?: string;
  checked?: boolean;
  listCountGetFlg: string; // リスト件数取得フラグ "0":未取得 "1":取得済み
  articleGetFlg: string; // 記事取得フラグ "0":未取得 "1":取得済み
  corpListGetFlg: string; // 企業リスト取得フラグ "0":未取得 "1":取得済み
  feedConditionGetFlg: string; // フィード条件取得フラグ "0":未取得 "1":取得済み
}

export interface CollectionMail extends BinderItem {
  checked: boolean;
}

export interface Item {
  companyCode: string;
  companyName: string;
  stkCode: string;
  source: string;
  companyKbn?: string;
  baseOptFlg?: string;
}

export interface Complements {
  label: string;
  text: string;
}

export interface ComplementsError {
  labelError: boolean;
  textError: boolean;
}

export interface CollectionCorpInfo {
  corpCode: string;
  corpName: string;
  source: string;
  stkCode: string;
  baseOptFlg?: string;
}

export interface BinderInfo {
  binderId: string;
  caption: string;
  corpList: CollectionCorpInfo[];
  description: string;
  andKeyword: string;
  orKeyword: string;
  notKeyword: string;
  includeRelatedCorp: string;
  createUserName?: string;
  sharedFlg: boolean;
  updateDate?: string; // 更新日： YYYY年MM月DD日
  publicStatus: string; // 公開ステータス "0":非公開 "1":公開
  pinnedFlg: string; // ピン留めフラグ "0":未ピン留め "1":ピン留め済み
  mediaList: Media[];
  definition: Complements[];
  background: Complements[];
  task: Complements[];
  technology: Complements[];
  topics: Complements[];
  query: string;
  mailAlertFlg: string; // メールアラートフラグ "0":無効 "1":有効
  searchType: string;
  regulation: Complements[];
  upperLimit: UpperLimit;
}

export interface UpperLimit {
  definition: Complements;
  background: Complements;
  task: Complements;
  technology: Complements;
  topics: Complements;
  regulation: Complements;
}

export interface Media {
  baseOptFlg: string;
  label: string;
  checked: boolean;
  value: string;
  mediaFeeType: string;
  hierarchy: string;
}

export interface Image {
  description: string;
  height: string;
  width: string;
  imagePath: string;
  style: string;
}

export interface Article {
  articleId: string;
  articleKbn: string;
  attachmentPath: string;
  cardId: string;
  corpName: string;
  dataExistsFlg: boolean;
  displayTime: string;
  highlight: Array<string>;
  highlightIndex: Array<string>;
  thumbPath: string;
  mediaCode: string;
  mediaName: string;
  title: string;
  description: string;
  keywords: Array<string>;
  sponserName: string;
  canonicalUrl: string;
  mediaFeeType: string;
  keyPdfFare: string;
  keyPdf: string;
  keyPpt: string;
  keyBodyFare: string;
  errorStatus?: string;
  errorDetailMessage?: string;
  // 設定されている場合、記事クリック時にポップアップを開かずこのURLへ直接遷移する（トランスクリプト等）
  redirectUrl?: string;
}

export interface ExternalPage {
  cardId: string;
  url: string;
  title: string;
  thumbPath: string;
  domain: string;
  favicon: string;
  description: string;
}

export interface MessageDto {
  msgCode: string;
  message: string;
}

export interface CategoryInfo {
  categoryCode: string;
  totalCount: number;
}

export interface Corporation {
  asrf: string;
  businessDescription: string;
  cardId: string;
  corpCode: string;
  corpIndustryKbn: string;
  corpName: string;
  corpType: string;
  dataExistsFlg: boolean;
  industryName: string;
  stockType: string;
  source: string;
  memo: string;
}

export const isCorporation = (arg: any): arg is Corporation => {
  return Object.prototype.hasOwnProperty.call(arg, "corpName");
};

export const isCorporationArray = (arg: any): arg is Corporation => {
  if (!isArray(arg)) return false;
  return isCorporation(arg[0]);
};

export interface Industry {
  cardId: string;
  corpIndustryKbn: string;
  dataExistsFlg: boolean;
  industryClassification: string;
  industryCode: string;
  industryDefinition: string;
  industryName: string;
  sectorReportPath: string;
  keyPpt: string;
  largeIndustryCode: string;
  middleIndustryCode: string;
}

export interface Statistic {
  area: string;
  areadescj: string;
  dataExistsFlg: boolean;
  cardId: string;
  schema: string;
  series: string;
  statisticsType: string;
  title: string;
  seriesDatas: {
    date: string;
    value: string;
  }[];
  areaSeriesData: {
    name: string;
    seriesData: {
      date: string;
      value: string;
    }[];
  }[];
  extFlg: ExtType;
  areaExtFlg: AreaExtType;
  seriesdescj: string;
  orgdescj: string;
}

export type BinderActionType =
  | "delete"
  | "delete_return_index"
  | "clone"
  | "clone_show_new"
  | "share"
  | "edit"
  | "new"
  | "publish";

export interface BinderActionSet {
  type: BinderActionType;
  binderInfo?: BinderInfo;
}

export type BinderType = "nikkei" | "personal";

export const isBinderType = (arg: any): arg is BinderType => {
  return arg === "nikkei" || arg === "personal";
};

// コレクションの追加情報
export interface CollectionAdditionalInfo {
  binderList: {
    binderInfo: {
      binderId: string; // コレクションID
      andKeyword?: string; // ANDキーワード
      notKeyword?: string;
      orKeyword?: string;
      mediaList?: Media[];
      query?: string;
      includeRelatedCorp?: string;
      searchType?: string;
      corpList?: CollectionCorpInfo[];
    };
    corpTotalCount?: string; // 企業数
    industryTotalCount?: string; // 業種数
    statisticsTotalCount?: string; // 統計数
    saveArticleTotalCount?: string; // 保存記事数
    externalPageTotalCount?: string; // 外部ページ数
    articleList?: Article[];
  }[];
}

// 一括検索用のコレクション情報
export interface CollectionAdditionalResultInfo {
  binderResultList: Omit<
    SearchListResultItem,
    "articleGetFlg" | "corpListGetFlg" | "feedConditionGetFlg"
  >[];
}

export interface SearchListResultItem {
  background: { label: string; text: string }[];
  binderId: string;
  caption: string;
  corpCount: string;
  corpList: { corpCode: string; corpName: string }[];
  createUserName: string;
  definition: { label: string; text: string }[];
  description: string;
  publicStatus: string;
  regulation: { label: string; text: string }[];
  task: { label: string; text: string }[];
  technology: { label: string; text: string }[];
  topics: { label: string; text: string }[];
  updateDate: string;
  listCountGetFlg: string; // リスト件数取得フラグ "0":未取得 "1":取得済み
  articleGetFlg: string; // 記事取得フラグ "0":未取得 "1":取得済み
  corpListGetFlg: string; // 企業リスト取得フラグ "0":未取得 "1":取得済み
  feedConditionGetFlg: string; // フィード条件取得フラグ "0":未取得 "1":取得済み
}
